import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  OnChanges,
  SimpleChanges,
  NgZone,
} from "@angular/core";
import { GoogleMap, MapInfoWindow } from "@angular/google-maps";
import { LocationData } from "src/app/models/tdsp/tdsp-custom-location";

@Component({
  selector: "app-location-selector",
  templateUrl: "./location-selector.component.html",
  styleUrls: ["./location-selector.component.scss"],
})
export class LocationSelectorComponent implements OnChanges {
  @Input() locations: LocationData[] = [];
  @ViewChild(MapInfoWindow) infoWindow!: MapInfoWindow;

  @Input() zoom = 6;
  @Input() centerRequest: { location: LocationData; zoom: number } | null =
    null;
  @Input() center: google.maps.LatLngLiteral = { lat: 20, lng: 0 }; // default world view;
  @Input() isEdit: boolean = false;

  @Output() radiusChanged = new EventEmitter<LocationData>();
  @Output() zoomChanged = new EventEmitter<number>();
  @Output() markerMoved = new EventEmitter<LocationData>();
  @Output() markerClicked = new EventEmitter<LocationData>();
  @Output() mapClicked = new EventEmitter<{
    lat: number;
    lng: number;
    name: string;
  }>();

  @ViewChild(GoogleMap) map: GoogleMap;

  mapOptions: google.maps.MapOptions = {
    mapTypeId: google.maps.MapTypeId.ROADMAP,
    zoomControl: true,
    scrollwheel: true,
    disableDoubleClickZoom: true,
    maxZoom: 18,
    minZoom: 2,
    fullscreenControl: false,
    streetViewControl: false,
  };

  circles: Map<number, google.maps.Circle> = new Map();
  markers: Map<number, google.maps.Marker> = new Map();
  selectedLocation: any = null;

  private geocoder: google.maps.Geocoder;

  constructor(private ngZone: NgZone) {}

  ngAfterViewInit() {
    this.geocoder = new google.maps.Geocoder();

    // Map click listener
    this.map.googleMap?.addListener("click", (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return;
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();

      this.geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        this.ngZone.run(() => {
          const name =
            status === "OK" && results[0]
              ? results[0].formatted_address
              : `Lat: ${lat}, Lng: ${lng}`;
          this.mapClicked.emit({ lat, lng, name });
        });
      });
    });

    if (this.locations.length > 0) {
      this.refreshMarkersAndCircles();
    }
  }

  openInfoWindow(marker, loc: any) {
    this.selectedLocation = loc;
    this.infoWindow.open(marker);
  }

  ngOnChanges(changes: SimpleChanges) {
    // Handle navigation requests from parent
    if (changes["centerRequest"] && this.centerRequest) {
      const { location, zoom } = this.centerRequest;
      this.center = { lat: location.lat, lng: location.lng };
      this.zoom = zoom;
    }

    // Handle marker/circle refresh
    if (changes["locations"] && this.locations) {
      this.refreshMarkersAndCircles();
    }
  }

  private refreshMarkersAndCircles() {
    this.clearMap();

    this.locations.forEach((loc) => {
      const marker = new google.maps.Marker({
        position: { lat: loc.lat, lng: loc.lng },
        map: this.map.googleMap!,
        title: loc.name,
        draggable: true,
      });

      const circle = new google.maps.Circle({
        center: { lat: loc.lat, lng: loc.lng },
        radius: loc.radius,
        map: this.map.googleMap!,
        editable: true,
      });

      this.markers.set(loc.id, marker);
      this.circles.set(loc.id, circle);

      // Circle radius changed
      circle.addListener("radius_changed", () => {
        const newRadius = Math.round(circle.getRadius());
        this.ngZone.run(() => {
          this.radiusChanged.emit({ ...loc, radius: newRadius });
          this.markerClicked.emit({ ...loc });
        });
      });

      // Marker dragged
      marker.addListener("dragend", () => {
        const newPos = marker.getPosition();
        if (newPos) {
          this.ngZone.run(() => {
            const lat = newPos.lat();
            const lng = newPos.lng();

            loc.lat = lat;
            loc.lng = lng;
            circle.setCenter(newPos);

            this.geocoder.geocode(
              { location: { lat, lng } },
              (results, status) => {
                this.ngZone.run(() => {
                  loc.name =
                    status === "OK" && results[0]
                      ? results[0].formatted_address
                      : `Lat: ${lat}, Lng: ${lng}`;
                  loc.title = loc.name;
                  this.markerMoved.emit({ ...loc });
                  this.markerClicked.emit({ ...loc });
                });
              }
            );
          });
        }
      });

      marker.addListener("click", () => {
        this.ngZone.run(() => {
          this.markerClicked.emit({ ...loc });
        });
      });

      circle.addListener("click", () => {
        this.ngZone.run(() => {
          this.markerClicked.emit({ ...loc });
        });
      });
    });
  }

  clearMap() {
    this.circles.forEach((c) => c.setMap(null));
    this.markers.forEach((m) => m.setMap(null));
    this.circles.clear();
    this.markers.clear();
  }

  onZoomChange() {
    const zoom = this.map.googleMap?.getZoom();
    if (zoom !== undefined) {
      this.zoomChanged.emit(zoom);
    }
  }

  /** Update circle radius live from parent */
  updateCircleRadius(location: LocationData) {
    if (this.isEdit) return;
    const circle = this.circles.get(location.id);
    if (circle) {
      circle.setRadius(location.radius);
    }
  }
}
